import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, web, errMsg } from "../../lib/api";
import { Switch } from "../ui/switch";
import { Btn } from "./kit";
import { Modal, Field, TextInput, Chips, numOrNull } from "./Form";

export const POSITIONS = ["PG", "SG", "SF", "PF", "C"];
const posList = (p) => (Array.isArray(p?.positions) && p.positions.length ? p.positions : p?.position ? [p.position] : []);

const toForm = (p) => ({
  name: p?.name ?? "",
  jersey: p?.jersey ?? "",
  pos1: posList(p)[0] ?? "",
  pos2: posList(p)[1] ?? "",
  practice_player: !!p?.practice_player,
  captain: !!p?.captain,
  starter: !!p?.starter,
  grad_year: p?.grad_year ?? "",
  feet: p?.height_in ? Math.floor(p.height_in / 12) : "",
  inches: p?.height_in ? Math.round(p.height_in % 12) : "",
  weight_lb: p?.weight_lb ?? "",
  gpa: p?.gpa ?? "",
  level: p?.level === "jv" ? "jv" : "varsity",
});

const toPayload = (f) => {
  const ft = numOrNull(f.feet);
  const inch = numOrNull(f.inches);
  const positions = [f.pos1, f.pos2].filter(Boolean);
  return {
    name: f.name.trim(),
    jersey: String(f.jersey).trim() || null,
    positions,
    position: positions[0] ?? null,
    grad_year: numOrNull(f.grad_year),
    height_in: ft === null && inch === null ? null : (ft ?? 0) * 12 + (inch ?? 0),
    weight_lb: numOrNull(f.weight_lb),
    gpa: numOrNull(f.gpa),
    level: f.level,
  };
};

export const PlayerForm = ({ open, onClose, teamId, player, onSaved }) => {
  const [f, setF] = useState(toForm(player));
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(toForm(player)); }, [open, player]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Player name is required");
    const gpa = numOrNull(f.gpa);
    if (gpa !== null && (gpa < 0 || gpa > 5)) return toast.error("GPA must be between 0 and 5");
    if (f.pos2 && !f.pos1) return toast.error("Pick a 1st position first");
    setBusy(true);
    try {
      const body = toPayload(f);
      const { data } = player
        ? await api.patch(`/teams/${teamId}/players/${player.id}`, body)
        : await api.post(`/teams/${teamId}/players`, body);
      const pid = player?.id ?? data?.id ?? data?.player?.id;
      if (pid && f.practice_player !== !!player?.practice_player) await web.put(`/teams/${teamId}/players/${pid}/practice`, { practice_player: f.practice_player });
      if (pid && f.captain !== !!player?.captain) await web.put(`/teams/${teamId}/players/${pid}/captain`, { captain: f.captain });
      if (pid && f.starter !== !!player?.starter) await web.put(`/teams/${teamId}/players/${pid}/starter`, { starter: f.starter });
      toast.success(player ? `${body.name} updated` : `${body.name} added to the roster`);
      onSaved?.(data);
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} testid="player-form-dialog" title={player ? "Edit player" : "Add player"} description="Changes show up in the GAMEDAY mobile app too.">
      <form onSubmit={submit} className="mt-2 space-y-4">
        <div className="grid grid-cols-[1fr_96px] gap-3">
          <Field label="Name"><TextInput data-testid="player-name-input" value={f.name} onChange={set("name")} placeholder="Jordan Reyes" autoFocus /></Field>
          <Field label="Jersey"><TextInput data-testid="player-jersey-input" value={f.jersey} onChange={set("jersey")} placeholder="23" maxLength={3} /></Field>
        </div>
        <Field label="Team level"><Chips testid="player-level" options={[["varsity", "Varsity"], ["jv", "JV"]]} value={f.level} onChange={set("level")} /></Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="1st position (shows on the roster)"><Chips testid="player-position-1" options={POSITIONS} value={f.pos1} onChange={(v) => setF((s) => ({ ...s, pos1: s.pos1 === v ? "" : v, pos2: s.pos2 === v ? "" : s.pos2 }))} /></Field>
          <Field label="2nd position (optional)"><Chips testid="player-position-2" options={POSITIONS.filter((x) => x !== f.pos1)} value={f.pos2} onChange={(v) => setF((s) => ({ ...s, pos2: s.pos2 === v ? "" : v }))} /></Field>
        </div>
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-3 text-sm text-slate-200">
            <Switch data-testid="player-practice-switch" checked={f.practice_player} onCheckedChange={(v) => setF((s) => ({ ...s, practice_player: v }))} className="data-[state=checked]:bg-[#39FF14]" />
            Practice player
          </label>
          <label className="flex items-center gap-3 text-sm text-slate-200">
            <Switch data-testid="player-captain-switch" checked={f.captain} onCheckedChange={(v) => setF((s) => ({ ...s, captain: v }))} className="data-[state=checked]:bg-[#39FF14]" />
            Team captain <span className="text-xs text-slate-400">(up to 2 per squad)</span>
          </label>
          <label className="flex items-center gap-3 text-sm text-slate-200">
            <Switch data-testid="player-starter-switch" checked={f.starter} onCheckedChange={(v) => setF((s) => ({ ...s, starter: v }))} className="data-[state=checked]:bg-[#39FF14]" />
            Starter <span className="text-xs text-slate-400">(up to 5 per squad)</span>
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label="Grad year"><TextInput data-testid="player-grad-input" type="number" inputMode="numeric" value={f.grad_year} onChange={set("grad_year")} placeholder="2027" /></Field>
          <Field label="Height ft"><TextInput data-testid="player-feet-input" type="number" min={4} max={7} value={f.feet} onChange={set("feet")} placeholder="6" /></Field>
          <Field label="In"><TextInput data-testid="player-inches-input" type="number" min={0} max={11} value={f.inches} onChange={set("inches")} placeholder="2" /></Field>
          <Field label="Weight lb"><TextInput data-testid="player-weight-input" type="number" value={f.weight_lb} onChange={set("weight_lb")} placeholder="180" /></Field>
        </div>
        <Field label="GPA" className="max-w-[140px]"><TextInput data-testid="player-gpa-input" type="number" step="0.01" min={0} max={5} value={f.gpa} onChange={set("gpa")} placeholder="3.5" /></Field>
        <div className="flex justify-end gap-2 pt-2">
          <Btn type="button" variant="ghost" onClick={onClose} data-testid="player-form-cancel">Cancel</Btn>
          <Btn type="submit" disabled={busy} data-testid="player-form-save">{busy ? "Saving…" : player ? "Save changes" : "Add player"}</Btn>
        </div>
      </form>
    </Modal>
  );
};
